import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Button } from "reactstrap";
import { useGetCustomerDeposits } from "../../../api/customer-deposits/service";
import { formatDateForDisplay } from "../../../utils/dateUtils";

const CustomerDepositHistory = ({
  activeColor,
  activeColorStyle,
  isLightTheme,
  customerId,
  customerDetail,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();

  const PRINT_ROOT_CLASS = "customer-deposit-print-root";
  const printStyles = `
  // Hide URL in print footer - note: users may need to disable "Headers and footers" in print dialog
  // @page {
  //     margin: 15mm;
  //     size: A4 portrait;
  //   }

    @media print {
      * {
        box-sizing: border-box;
        font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
      }

      body {
        background: #ffffff;
        color: #0f172a;
        margin: 0;
        padding: 0;
      }

      body * {
        visibility: hidden;
      }

      .${PRINT_ROOT_CLASS},
      .${PRINT_ROOT_CLASS} * {
        visibility: visible;
      }

      .${PRINT_ROOT_CLASS} {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        background: #ffffff;
        z-index: 9999;
        padding: 0;
        margin: 0;
        page-break-after: avoid;
        page-break-before: avoid;
      }

      .${PRINT_ROOT_CLASS} .print-wrapper {
        max-width: 960px;
        margin: 0 auto;
        padding: 0 12mm;
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        page-break-after: avoid;
        page-break-before: avoid;
      }

      .${PRINT_ROOT_CLASS} .print-header {
        text-align: center;
        padding: 18px 0 24px;
        border-bottom: 2px solid #1f2937;
        margin-bottom: 28px;
      }

      .${PRINT_ROOT_CLASS} .print-brand {
        font-size: 22px;
        font-weight: 700;
        letter-spacing: 0.18em;
        text-transform: uppercase;
      }

      .${PRINT_ROOT_CLASS} .print-meta {
        margin-top: 6px;
        font-size: 11px;
        color: #475569;
      }

      .${PRINT_ROOT_CLASS} .print-section {
        margin-bottom: 24px;
        page-break-inside: avoid;
        page-break-after: auto;
      }

      .${PRINT_ROOT_CLASS} .print-section__title {
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin-bottom: 18px;
        padding-bottom: 10px;
        border-bottom: 1px solid #cbd5f5;
      }

      .${PRINT_ROOT_CLASS} .print-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px 36px;
      }

      .${PRINT_ROOT_CLASS} .print-field {
        border-bottom: 1px solid #e2e8f0;
        padding-bottom: 10px;
      }

      .${PRINT_ROOT_CLASS} .print-field__label {
        display: block;
        font-size: 11px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #64748b;
        margin-bottom: 4px;
        font-weight: 600;
      }

      .${PRINT_ROOT_CLASS} .print-field__value {
        font-size: 13px;
        font-weight: 600;
        color: #0f172a;
      }

      .${PRINT_ROOT_CLASS} .print-card {
        border: 1px solid #d4d7e1;
        border-radius: 10px;
        padding: 22px 28px;
        page-break-inside: avoid;
        page-break-after: avoid;
        margin-bottom: 0;
      }

      .${PRINT_ROOT_CLASS} .print-footer {
        text-align: center;
        font-size: 11px;
        color: #6b7280;
        padding-top: 12px;
        border-top: 1px solid #d1d5db;
        page-break-after: avoid;
        margin-top: 20px;
      }
      
      /* Prevent multiple pages - ensure content fits on one page */
      .${PRINT_ROOT_CLASS} .print-wrapper {
        overflow: visible;
        height: auto;
        max-height: 100vh;
      }
      
      /* Hide everything except print content */
      body > *:not(.${PRINT_ROOT_CLASS}) {
        display: none !important;
      }
      
      /* Force single page */
      .${PRINT_ROOT_CLASS} {
        page-break-after: avoid !important;
        page-break-before: avoid !important;
        orphans: 999;
        widows: 999;
      }
      
      .${PRINT_ROOT_CLASS} * {
        page-break-inside: avoid;
        orphans: 999;
        widows: 999;
      }
    }
  `;

  const { data: depositsData, isLoading, isError } = useGetCustomerDeposits(
    customerId,
    !!customerId
  );

  // Extract deposits from API response - ensure it's always an array
  // API returns { results: Array, pagination: {...} }
  const deposits = useMemo(() => {
    if (!depositsData) return [];
    
    // If depositsData is already an array, return it
    if (Array.isArray(depositsData)) return depositsData;
    
    // If depositsData has a results property that is an array, return it
    if (depositsData.results && Array.isArray(depositsData.results)) return depositsData.results;
    
    // If depositsData has a data property that is an array, return it
    if (depositsData.data && Array.isArray(depositsData.data)) return depositsData.data;
    
    // Otherwise return empty array
    return [];
  }, [depositsData]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getCurrentDate = () => {
    const now = new Date();
    return now.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const customerInfoFields = useMemo(() => [
    {
      label: t("customerDetail.depositHistory.name"),
      value: customerDetail?.full_name || customerDetail?.name || "-",
    },
    {
      label: t("customerDetail.depositHistory.address1"),
      value: customerDetail?.address_1 || "-",
    },
    {
      label: t("customerDetail.depositHistory.address2"),
      value: customerDetail?.address_2 || "-",
    },
    {
      label: t("customerDetail.depositHistory.city"),
      value: customerDetail?.city || "-",
    },
    {
      label: t("customerDetail.depositHistory.state"),
      value:
        customerDetail?.state?.abbreviation ||
        customerDetail?.state ||
        "-",
    },
    {
      label: t("customerDetail.depositHistory.zip"),
      value: customerDetail?.zip || "-",
    },
    {
      label: t("customerDetail.depositHistory.phone", "Phone"),
      value: customerDetail?.cell_phone || customerDetail?.home_phone || "-",
    },
    {
      label: t("customerDetail.depositHistory.email", "Email"),
      value: customerDetail?.email || customerDetail?.alt_email || "-",
    },
  ], [customerDetail, t]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  const buildDepositFields = (deposit) => [
    {
      label: t("customerDetail.depositHistory.tranId", "Tran ID"),
      value: deposit.id || deposit.tran_id || "-",
    },
    {
      label: t("customerDetail.depositHistory.postDate", "Post Date"),
      value: deposit.post_date
        ? formatDateForDisplay(deposit.post_date)
        : deposit.postDate
        ? formatDateForDisplay(deposit.postDate)
        : deposit.date
        ? formatDateForDisplay(deposit.date)
        : "-",
    },
    {
      label: t("customerDetail.depositHistory.tranCode", "Tran Code"),
      value: deposit.tran_code || deposit.tranCode || deposit.transaction_code || "-",
    },
    {
      label: t("customerDetail.depositHistory.amountPaid", "Amount Paid"),
      value: formatCurrency(deposit.amount || deposit.amount_paid),
    },
    {
      label: t("customerDetail.depositHistory.form", "Form"),
      value: deposit.form || deposit.payment_form || deposit.paymentForm || "-",
    },
    {
      label: t("customerDetail.depositHistory.refNumber", "Ref Number"),
      value: deposit.ref_number || deposit.refNumber || "-",
    },
    {
      label: t("customerDetail.depositHistory.effectiveDate", "Effective Date"),
      value: deposit.effective_date
        ? formatDateForDisplay(deposit.effective_date)
        : deposit.effectiveDate
        ? formatDateForDisplay(deposit.effectiveDate)
        : "-",
    },
    {
      label: t("customerDetail.depositHistory.changeGiven", "Change Given"),
      value: formatCurrency(deposit.change_due || deposit.changeDue || deposit.change_given),
    },
    {
      label: t("customerDetail.depositHistory.salesLocation", "Sales Location"),
      value: deposit.sales_location || deposit.salesLocation || deposit.location || "-",
    },
    {
      label: t("customerDetail.depositHistory.company", "Company"),
      value: deposit.bk_company || deposit.bkCompany || deposit.company || "-",
    },
  ];

  const renderFieldsHtml = (fields) =>
    fields
      .map(
        (field) => `
          <div class="print-field">
            <span class="print-field__label">${field.label}</span>
            <span class="print-field__value">${field.value || "-"}</span>
          </div>
        `
      )
      .join("");

  const buildPrintDocument = (deposit) => `
    <div class="print-wrapper">
      <header class="print-header">
        <div class="print-brand">DealerTIQ</div>
        <div class="print-meta">${getCurrentDate()}</div>
      </header>

      <section class="print-section">
        <h2 class="print-section__title">${t("customerDetail.depositHistory.customerInfo")}</h2>
        <div class="print-grid">
          ${renderFieldsHtml(customerInfoFields)}
        </div>
      </section>

      <section class="print-section">
        <h2 class="print-section__title">${t("customerDetail.depositHistory.depositHistory")}</h2>
        <div class="print-card">
          <div class="print-grid">
            ${renderFieldsHtml(buildDepositFields(deposit))}
          </div>
        </div>
      </section>

      <footer class="print-footer">DealerTIQ Report • Page 1</footer>
    </div>
  `;

  const handlePrintReceipt = (deposit) => {
    if (typeof document === "undefined") {
      window.print();
      return;
    }

    const printRoot = document.createElement("div");
    printRoot.className = PRINT_ROOT_CLASS;
    printRoot.innerHTML = buildPrintDocument(deposit);

    const styleTag = document.createElement("style");
    styleTag.type = "text/css";
    styleTag.appendChild(document.createTextNode(printStyles));

    document.body.appendChild(printRoot);
    document.head.appendChild(styleTag);

    const cleanup = () => {
      if (printRoot.parentNode) {
        printRoot.parentNode.removeChild(printRoot);
      }
      if (styleTag.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }
    };

    requestAnimationFrame(() => {
      window.print();
      setTimeout(cleanup, 100);
    });
  };

  return (
    <div className="print-content">

      {/* CUSTOMER INFO */}
      <div className="customer-info" style={{ marginBottom: 10 }}>
        <h6 style={{
          marginBottom: 10,
          color: isLightTheme ? "#1e1e2f" : "#ffffff",
          fontWeight: 600,
          fontSize: 12,
          textTransform: "uppercase"
        }}>
          {t("customerDetail.depositHistory.customerInfo")}
        </h6>
        <Row>
          <Col md="6">
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                {t("customerDetail.depositHistory.name")}: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                  {customerDetail?.full_name || customerDetail?.name || "-"}
                </span>
              </span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                {t("customerDetail.depositHistory.address1")}: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                  {customerDetail?.address_1 || "-"}
                </span>
              </span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                {t("customerDetail.depositHistory.address2")}: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                  {customerDetail?.address_2 || "-"}
                </span>
              </span>
            </div>
          </Col>
          <Col md="6">
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                {t("customerDetail.depositHistory.city")}: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                  {customerDetail?.city || "-"}
                </span>
              </span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                {t("customerDetail.depositHistory.state")}: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                  {customerDetail?.state?.abbreviation || customerDetail?.state || "-"}
                </span>
              </span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                {t("customerDetail.depositHistory.zip")}: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                  {customerDetail?.zip || "-"}
                </span>
              </span>
            </div>
          </Col>
        </Row>
      </div>

      {/* DEPOSIT HISTORY */}
      <div className="deposit-history">
        <h6 style={{
          marginBottom: 20,
          color: isLightTheme ? "#1e1e2f" : "#ffffff",
          fontWeight: 600,
          fontSize: 12,
          textTransform: "uppercase"
        }}>
          {t("customerDetail.depositHistory.depositHistory")}
        </h6>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: 40, fontSize: 12 }}>
            {t("customerDetail.depositHistory.loading")}
          </div>
        ) : isError ? (
          <div style={{ textAlign: "center", padding: 40, fontSize: 12, color: "#ff5c5c" }}>
            {t("customerDetail.depositHistory.error")}
          </div>
        ) : deposits.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, fontSize: 12 }}>
            {t("customerDetail.depositHistory.noHistory")}
          </div>
        ) : (
          deposits.map((deposit, index) => (
            <div
              key={deposit.id || deposit.tran_id || index}
              className="deposit-card"
              style={{
                marginBottom: 30,
                padding: 16,
                backgroundColor: isLightTheme ? "#f8f9fa" : "#2b2b3d",
                borderRadius: 4,
                border: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
              }}
            >
              <Row>
                <Col md="6">
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      TranID: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.tran_id || deposit.id || "-"}
                      </span>
                    </span>
                  </div>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Post Date: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {formatDateTime(deposit.post_date || deposit.postDate || deposit.date)}
                      </span>
                    </span>
                  </div>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Tran Code: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.tran_id || deposit.tran_code || deposit.tranCode || deposit.transaction_code || "-"}
                      </span>
                    </span>
                  </div>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Amount Paid: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {formatCurrency(deposit.amount_received || deposit.amount_paying || deposit.amount || deposit.amount_paid)}
                      </span>
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Form: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.payment_method || deposit.form || deposit.payment_form || deposit.paymentForm || "-"}
                      </span>
                    </span>
                  </div>
                </Col>
                <Col md="6">
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Ref Number: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.ref_number || deposit.refNumber || "-"}
                      </span>
                    </span>
                  </div>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Effect Date: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.effective_date ? formatDateForDisplay(deposit.effective_date) : deposit.effectiveDate ? formatDateForDisplay(deposit.effectiveDate) : "-"}
                      </span>
                    </span>
                  </div>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Change Given: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {formatCurrency(deposit.change_due || deposit.changeDue || deposit.change_given)}
                      </span>
                    </span>
                  </div>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${isLightTheme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}`
                  }}>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      Sales Location: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.sales_location?.channel?.name || 
                         deposit.sales_location?.name ||
                         (typeof deposit.sales_location === 'string' ? deposit.sales_location : null) ||
                         deposit.salesLocation || 
                         deposit.location || "-"}
                      </span>
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: isLightTheme ? "#666" : "#a0a0b2" }}>
                      BK Company: <span style={{ color: isLightTheme ? "#1e1e2f" : "#ffffff" }}>
                        {deposit.bk_company || deposit.bkCompany || deposit.company || "-"}
                      </span>
                    </span>
                  </div>
                </Col>
              </Row>
              <div className="no-print" style={{ textAlign: "center", marginTop: 20 }}>
                <Button
                  onClick={() => handlePrintReceipt(deposit)}
                  style={{
                    ...activeColorStyle,
                    color: "white",
                    padding: "8px 24px",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 4
                  }}
                >
                  {t("customerDetail.depositHistory.printReceipt")}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CustomerDepositHistory;
